import { useCallback, useEffect, useEffectEvent, useRef } from 'react';

/**
 * Runs `callback` on a fixed interval. The callback is wrapped in an effect event, so changing it does not restart
 * the interval — only `intervalMs` does.
 *
 * @param callback - Function to invoke on each tick.
 * @param intervalMs - Interval in milliseconds, or `null` to disable the interval entirely.
 * @returns A function that clears the interval.
 */
export function useInterval(callback: () => void, intervalMs: number | null) {
	const id = useRef<number | undefined>(undefined);

	const handleClearInterval = useCallback(() => {
		window.clearInterval(id.current);
	}, []);

	const onInterval = useEffectEvent(callback);
	useEffect(() => {
		if (intervalMs === null) {
			return;
		}

		id.current = window.setInterval(onInterval, intervalMs);
		return handleClearInterval;
	}, [intervalMs, handleClearInterval]);

	return handleClearInterval;
}
